// ============================================================
//  TEKLED AI Lighting Advisor Widget v2
//  — Real product catalogue (60 products)
//  — Smarter qualifying questions
//  — Session memory (chat persists while browsing)
//  — Auto proactive pop-up after 30 seconds
// ============================================================

(function () {
  'use strict';

  const PROXY_URL = window.TEKLED_PROXY_URL || 'https://jovial-semolina-23c992.netlify.app/.netlify/functions/lighting-chat';
  const SESSION_KEY = 'tkl_chat_history';
  const SHOWN_KEY = 'tkl_greeted';

  let isOpen = false;
  let isLoading = false;
  let conversationHistory = [];

  // ── RESTORE SESSION ──────────────────────────────────────────
  try {
    const saved = sessionStorage.getItem(SESSION_KEY);
    if (saved) conversationHistory = JSON.parse(saved);
  } catch(e) {}

  // ── INJECT STYLES ────────────────────────────────────────────
  const style = document.createElement('style');
  style.textContent = `
    #tkl-launcher {
      position: fixed; bottom: 24px; left: 24px;
      width: 62px; height: 62px; border-radius: 50%;
      background: linear-gradient(135deg, #1a1a2e, #16213e);
      border: 2px solid #f4b942; cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 4px 20px rgba(244,185,66,0.4);
      z-index: 99999; transition: transform 0.2s, box-shadow 0.2s;
    }
    #tkl-launcher:hover { transform: scale(1.08); box-shadow: 0 6px 28px rgba(244,185,66,0.55); }
    #tkl-launcher svg { width: 26px; height: 26px; }

    #tkl-pulse {
      position: fixed; bottom: 24px; left: 24px;
      width: 62px; height: 62px; border-radius: 50%;
      background: rgba(244,185,66,0.3);
      z-index: 99998; pointer-events: none;
      animation: tklPulse 2s ease-out 30s 3;
    }
    @keyframes tklPulse {
      0% { transform: scale(1); opacity: 0.8; }
      100% { transform: scale(1.7); opacity: 0; }
    }

    #tkl-bubble {
      position: fixed; bottom: 94px; left: 24px;
      background: #1a1a2e; color: #fff;
      border: 1.5px solid #f4b942; border-radius: 12px;
      padding: 10px 14px; font-size: 13px; line-height: 1.4;
      max-width: 220px; z-index: 99997;
      box-shadow: 0 4px 16px rgba(0,0,0,0.2);
      opacity: 0; transform: translateY(8px);
      transition: opacity 0.3s ease, transform 0.3s ease;
      pointer-events: none; font-family: inherit;
    }
    #tkl-bubble.tkl-show { opacity: 1; transform: translateY(0); pointer-events: all; cursor: pointer; }
    #tkl-bubble::after {
      content: ''; position: absolute; bottom: -8px; left: 20px;
      width: 0; height: 0;
      border-left: 8px solid transparent;
      border-right: 8px solid transparent;
      border-top: 8px solid #f4b942;
    }

    #tkl-widget {
      position: fixed; bottom: 98px; left: 24px;
      width: 360px; max-height: 580px;
      background: #fff; border-radius: 18px;
      box-shadow: 0 12px 48px rgba(0,0,0,0.18);
      display: flex; flex-direction: column; overflow: hidden;
      z-index: 99998; opacity: 0; transform: translateY(14px) scale(0.97);
      pointer-events: none; transition: opacity 0.25s ease, transform 0.25s ease;
    }
    #tkl-widget.tkl-open { opacity: 1; transform: translateY(0) scale(1); pointer-events: all; }

    .tkl-header {
      background: linear-gradient(135deg, #1a1a2e, #16213e);
      padding: 14px 16px; display: flex; align-items: center; gap: 10px; flex-shrink: 0;
    }
    .tkl-hicon {
      width: 36px; height: 36px; border-radius: 50%;
      background: rgba(244,185,66,0.15); border: 1.5px solid rgba(244,185,66,0.5);
      display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    }
    .tkl-hicon svg { width: 17px; height: 17px; }
    .tkl-htitle { color: #fff; font-size: 13.5px; font-weight: 700; }
    .tkl-hsub { color: rgba(244,185,66,0.85); font-size: 10.5px; margin-top: 1px; }
    .tkl-hbtns { margin-left: auto; display: flex; gap: 4px; }
    .tkl-hbtn {
      background: none; border: none; cursor: pointer;
      color: rgba(255,255,255,0.45); padding: 4px; border-radius: 5px;
      transition: color 0.15s; line-height: 1; font-size: 11px;
    }
    .tkl-hbtn:hover { color: #fff; }

    .tkl-msgs {
      flex: 1; overflow-y: auto; padding: 12px;
      display: flex; flex-direction: column; gap: 8px; scroll-behavior: smooth;
    }
    .tkl-msgs::-webkit-scrollbar { width: 3px; }
    .tkl-msgs::-webkit-scrollbar-thumb { background: #e0e0e0; border-radius: 3px; }

    .tkl-msg {
      max-width: 88%; padding: 9px 12px; border-radius: 14px;
      font-size: 13px; line-height: 1.55; animation: tklIn 0.2s ease;
      font-family: inherit;
    }
    @keyframes tklIn { from { opacity:0; transform:translateY(5px); } to { opacity:1; transform:translateY(0); } }
    .tkl-msg.tkl-bot { background: #f4f6fa; color: #1a1a2e; border-bottom-left-radius: 3px; align-self: flex-start; }
    .tkl-msg.tkl-user { background: #1a1a2e; color: #fff; border-bottom-right-radius: 3px; align-self: flex-end; }
    .tkl-msg.tkl-bot a { color: #c8920a; font-weight: 600; text-decoration: underline; word-break: break-all; }
    .tkl-msg.tkl-bot strong { color: #1a1a2e; }

    .tkl-typing {
      display: flex; align-items: center; gap: 4px;
      padding: 9px 12px; background: #f4f6fa; border-radius: 14px;
      border-bottom-left-radius: 3px; align-self: flex-start; width: 52px;
    }
    .tkl-typing span {
      width: 6px; height: 6px; background: #b0b8c8; border-radius: 50%;
      animation: tklBounce 1.2s infinite;
    }
    .tkl-typing span:nth-child(2) { animation-delay: 0.2s; }
    .tkl-typing span:nth-child(3) { animation-delay: 0.4s; }
    @keyframes tklBounce { 0%,60%,100%{transform:translateY(0)} 30%{transform:translateY(-5px)} }

    .tkl-quick { padding: 0 12px 8px; display: flex; flex-wrap: wrap; gap: 5px; flex-shrink: 0; }
    .tkl-qbtn {
      background: #fff; border: 1.5px solid #e2e5ed; border-radius: 16px;
      padding: 5px 11px; font-size: 11.5px; cursor: pointer; color: #1a1a2e;
      font-weight: 500; transition: background 0.15s, border-color 0.15s;
      font-family: inherit; white-space: nowrap;
    }
    .tkl-qbtn:hover { background: #fff9ec; border-color: #f4b942; }

    .tkl-input-row {
      padding: 7px 10px 10px; display: flex; gap: 7px; flex-shrink: 0;
      border-top: 1px solid #f0f2f6;
    }
    .tkl-input {
      flex: 1; border: 1.5px solid #e2e5ed; border-radius: 20px;
      padding: 8px 13px; font-size: 13px; outline: none; color: #1a1a2e;
      background: #fafbfc; transition: border-color 0.15s; font-family: inherit;
    }
    .tkl-input:focus { border-color: #f4b942; background: #fff; }
    .tkl-input::placeholder { color: #aeb5c0; }
    .tkl-send {
      width: 36px; height: 36px; border-radius: 50%;
      background: linear-gradient(135deg, #f4b942, #e0a030);
      border: none; cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0; transition: opacity 0.15s, transform 0.15s;
      box-shadow: 0 2px 8px rgba(244,185,66,0.4);
    }
    .tkl-send:hover { opacity: 0.88; transform: scale(1.05); }
    .tkl-send:disabled { opacity: 0.35; cursor: default; transform: none; }
    .tkl-send svg { width: 14px; height: 14px; }
    .tkl-footer { text-align: center; font-size: 10px; color: #c0c6d0; padding-bottom: 7px; letter-spacing: 0.03em; }

    @media (max-width: 480px) {
      #tkl-widget { width: calc(100vw - 20px); left: 10px; bottom: 88px; }
      #tkl-launcher { left: 16px; bottom: 16px; }
      #tkl-pulse { left: 16px; bottom: 16px; }
      #tkl-bubble { left: 16px; bottom: 90px; }
    }
  `;
  document.head.appendChild(style);

  // ── BUILD HTML ───────────────────────────────────────────────
  document.body.insertAdjacentHTML('beforeend', `
    <div id="tkl-pulse"></div>
    <div id="tkl-bubble" onclick="document.getElementById('tkl-launcher').click()">
      💡 Need help choosing the right light? Tell me about your space!
    </div>
    <div id="tkl-launcher" title="Ask our Lighting Advisor">
      <svg viewBox="0 0 24 24" fill="none" stroke="#f4b942" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M9 18h6M10 22h4M12 2a7 7 0 0 1 7 7c0 2.5-1 4.5-3 6l-1 1H9l-1-1C6 13.5 5 11.5 5 9a7 7 0 0 1 7-7z"/>
      </svg>
    </div>

    <div id="tkl-widget">
      <div class="tkl-header">
        <div class="tkl-hicon">
          <svg viewBox="0 0 24 24" fill="none" stroke="#f4b942" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 18h6M10 22h4M12 2a7 7 0 0 1 7 7c0 2.5-1 4.5-3 6l-1 1H9l-1-1C6 13.5 5 11.5 5 9a7 7 0 0 1 7-7z"/>
          </svg>
        </div>
        <div>
          <div class="tkl-htitle">TEKLED Lighting Advisor</div>
          <div class="tkl-hsub">Powered by AI · tekled.co.uk</div>
        </div>
        <div class="tkl-hbtns">
          <button class="tkl-hbtn" id="tkl-reset" title="Start new chat">↺</button>
          <button class="tkl-hbtn" id="tkl-close" title="Close">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      </div>
      <div class="tkl-msgs" id="tkl-msgs"></div>
      <div class="tkl-quick" id="tkl-quick"></div>
      <div class="tkl-input-row">
        <input class="tkl-input" id="tkl-input" type="text" placeholder="e.g. low ceiling kitchen, warm light…" />
        <button class="tkl-send" id="tkl-send">
          <svg viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
          </svg>
        </button>
      </div>
      <div class="tkl-footer">TEKLED AI Lighting Advisor · tekled.co.uk</div>
    </div>
  `);

  // ── PROACTIVE POP-UP after 30 seconds ────────────────────────
  setTimeout(function() {
    try {
      if (!sessionStorage.getItem(SHOWN_KEY) && !isOpen) {
        var bubble = document.getElementById('tkl-bubble');
        if (bubble) {
          bubble.classList.add('tkl-show');
          sessionStorage.setItem(SHOWN_KEY, '1');
          setTimeout(function() { bubble.classList.remove('tkl-show'); }, 6000);
        }
      }
    } catch(e) {}
  }, 30000);

  // ── WIRE UP EVENTS ───────────────────────────────────────────
  document.getElementById('tkl-launcher').addEventListener('click', toggleWidget);
  document.getElementById('tkl-close').addEventListener('click', toggleWidget);
  document.getElementById('tkl-reset').addEventListener('click', resetChat);
  document.getElementById('tkl-send').addEventListener('click', sendMessage);
  document.getElementById('tkl-input').addEventListener('keydown', function (e) {
    if (e.key === 'Enter') sendMessage();
  });

  function toggleWidget() {
    isOpen = !isOpen;
    document.getElementById('tkl-widget').classList.toggle('tkl-open', isOpen);
    document.getElementById('tkl-bubble').classList.remove('tkl-show');
    if (isOpen) {
      if (conversationHistory.length === 0) sendGreeting();
      else renderHistory();
      setTimeout(function() { document.getElementById('tkl-input').focus(); }, 280);
    }
  }

  function resetChat() {
    conversationHistory = [];
    try { sessionStorage.removeItem(SESSION_KEY); } catch(e) {}
    document.getElementById('tkl-msgs').innerHTML = '';
    document.getElementById('tkl-quick').innerHTML = '';
    sendGreeting();
  }

  function renderHistory() {
    var c = document.getElementById('tkl-msgs');
    c.innerHTML = '';
    conversationHistory.forEach(function(msg) {
      appendMsg(msg.role === 'user' ? 'user' : 'bot', msg.content, true);
    });
  }

  function sendGreeting() {
    appendMsg('bot', '👋 Hi! I\'m TEKLED\'s AI Lighting Advisor.\n\nTell me about the space you want to light — the room, ceiling height, and the atmosphere you\'re after — and I\'ll recommend specific products from our range with direct links and prices.');
    showQuick(['Kitchen', 'Living Room', 'Bedroom', 'Bathroom', 'Garden / Outdoor', 'Office', 'Hallway', 'Dining Room']);
  }

  function showQuick(opts) {
    var q = document.getElementById('tkl-quick');
    q.innerHTML = '';
    opts.forEach(function (o) {
      var btn = document.createElement('button');
      btn.className = 'tkl-qbtn';
      btn.textContent = o;
      btn.addEventListener('click', function () {
        q.innerHTML = '';
        handleMsg(o);
      });
      q.appendChild(btn);
    });
  }

  function sendMessage() {
    var input = document.getElementById('tkl-input');
    var text = input.value.trim();
    if (!text || isLoading) return;
    input.value = '';
    document.getElementById('tkl-quick').innerHTML = '';
    handleMsg(text);
  }

  async function handleMsg(text) {
    appendMsg('user', text);
    conversationHistory.push({ role: 'user', content: text });
    saveSession();
    setLoading(true);
    var typingEl = appendTyping();

    try {
      var res = await fetch(PROXY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: conversationHistory })
      });
      var data = await res.json();
      removeEl(typingEl);

      if (data.error) {
        appendMsg('bot', '⚠️ Something went wrong. Please try again or contact us at tekled.co.uk');
      } else {
        var reply = data.content[0].text;
        conversationHistory.push({ role: 'assistant', content: reply });
        saveSession();
        appendMsg('bot', reply);
        showQuick(['Different room', 'Lower budget options', 'Warm vs cool light?', 'Dimmable options?', 'Outdoor options', 'Start again ↺']);
      }
    } catch (err) {
      removeEl(typingEl);
      appendMsg('bot', '⚠️ Could not connect. Please check your connection and try again.');
    }
    setLoading(false);
  }

  function saveSession() {
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(conversationHistory)); } catch(e) {}
  }

  function appendMsg(role, text, silent) {
    var c = document.getElementById('tkl-msgs');
    var el = document.createElement('div');
    el.className = 'tkl-msg tkl-' + role;
    var html = text
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
      .replace(/\*\*(.+?)\*\*/g,'<strong>$1</strong>')
      .replace(/(https?:\/\/[^\s<\)\]]+)/g,'<a href="$1" target="_blank">$1</a>')
      .replace(/\n/g,'<br>');
    el.innerHTML = html;
    if (silent) el.style.animation = 'none';
    c.appendChild(el);
    c.scrollTop = c.scrollHeight;
  }

  function appendTyping() {
    var c = document.getElementById('tkl-msgs');
    var el = document.createElement('div');
    el.className = 'tkl-typing';
    el.innerHTML = '<span></span><span></span><span></span>';
    c.appendChild(el);
    c.scrollTop = c.scrollHeight;
    return el;
  }

  function removeEl(el) { if (el && el.parentNode) el.parentNode.removeChild(el); }

  function setLoading(state) {
    isLoading = state;
    document.getElementById('tkl-send').disabled = state;
    document.getElementById('tkl-input').disabled = state;
  }

})();
